<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { LiveReview, VideoEmbed } from '../interfaces/listing'
import { ListingTabs } from '../enums/listingTabs'
import HiddenGemz from '@/Components/hidden-gemz/HiddenGemz.vue'
import { User } from '@/types'
import { HiddenGemzType } from '@/Interfaces/Common'
import { useWindowSize } from '@vueuse/core'
import { AdData } from '@/Pages/the-city/interfaces/City'
import TheCityAds from '@/Pages/the-city/components/AdFeatures/TheCityAds.vue'

import AppModal from '@/Components/the-modal/AppModal.vue'
import TheIcon from '@/Components/TheIcon.vue'
import DescriptionTabModalContent from './DescriptionTabModalContent.vue'

type Props = {
    liveReview: LiveReview | null;
    description: string | null
    video?: VideoEmbed
    conditions: string | null,
    bookHostelsHref: string | undefined,
    hostelIsClosed: boolean,
    user: User | null,
    hiddenGemz?: HiddenGemzType[],
    locationName: string,
    cityName: string,
    adForListing?: AdData
}

const props = defineProps<Props>()

const emit = defineEmits(['tabsSwitchAndScroll'])

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }
    return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ')
}

const formattedDate = ref('')

onMounted(() => {
    if (props.liveReview?.reviewDate) {
        formattedDate.value = formatDate(props.liveReview.reviewDate)
    }
})

const { width } = useWindowSize()
const numberOfDisplayedPictures = ref(0)

onMounted( () => {
    
    if( width.value > 991 ) {
        numberOfDisplayedPictures.value = 5 
    } else if( width.value > 768 ) {
        numberOfDisplayedPictures.value = 4
    } else if( width.value > 576 ) {
        numberOfDisplayedPictures.value = 3
    } else {
        numberOfDisplayedPictures.value = 2
    }
})

const isModal = ref(false) 

const showModal = (): void => {
    isModal.value = !isModal.value  
}

const displayedImages = computed(() => {
    return props.liveReview?.images.slice(0, numberOfDisplayedPictures.value) || []
})

const extraImagesCount = computed(() => {
    return props.liveReview ? props.liveReview.images.length - numberOfDisplayedPictures.value : 0
})

const closeModalAndScrollToPriceTab = ():void => {
    showModal()
    emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)
}

</script>

<template>
    <div class="description-tab">
        <div v-if="hiddenGemz && hiddenGemz.length">
            <HiddenGemz
                v-for="(hg, key) in hiddenGemz"
                :key
                :="hg"
                :user="user"
                :is-single-listing="true"
                class="p-3 mb-lg-5 mb-3"
                @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
            />
        </div>
        
        <div v-if="adForListing" class="d-inline-block">
            <TheCityAds
                :url="adForListing.url"
                :image="adForListing.image"
                :content="adForListing.content"
                class="p-3 mb-lg-5 mb-3"
                @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
            />
        </div>
        <h3 v-if="!liveReview?.editedReview && !description && !video?.iframe && !video?.schema">
            No descriptions
        </h3>

        <div v-if="liveReview?.editedReview">
            <h3 class="mb-2 description-tab-main-liveReview-title">
                Real Traveler Insights: Unbiased Review of {{ locationName }} in {{ cityName }}
            </h3>

            <p class="tx-small lh-lg font-italic cl-subtext">
                At Hostelz.com, our reviews are based on firsthand experiences from travelers who’ve actually been there. Each review is genuine, unbiased, and includes real photos to help you make informed decisions.
            </p>
        </div>

        <div
            v-if="liveReview?.reviewDate && liveReview?.editedReview"
            class="mb-5 d-flex flex-sm-row flex-column align-items-sm-center justify-content-between"
        >
            <span v-if="liveReview?.user?.name" class="description-tab-user-rank pre-title d-block d-sm-none mb-1">
                Hostelz.com Staff Reviewer
            </span>
            <div class="d-flex align-items-center mb-1 mb-sm-0">
                <div v-if="liveReview?.user?.image" class="description-tab-user-avatar flex-shrink-0 mr-2">
                    <img :src="liveReview.user?.image" :alt="liveReview.user?.name">
                </div>

                <div v-if="liveReview?.user?.name" class="d-flex flex-column">
                    <h6 class="mb-0 description-tab-user-name">
                        {{ liveReview.user?.name }}
                    </h6>
                    <span class="description-tab-user-rank pre-title d-none d-sm-block">
                        Hostelz.com Staff Reviewer
                    </span>
                </div>
            </div>
            <div v-if="liveReview?.reviewDate">
                <div class="description-tab-date pre-title cl-subtext">
                    Published on {{ formattedDate }}
                </div>
            </div>
        </div>
        
        <div
            v-if="liveReview?.editedReview"
            class="description-tab-live-review mb-3"
            v-html="liveReview.editedReview"
        />

        <div v-if="displayedImages" class="d-flex description-tab-bottom-section-right-img-wrap-bottom mb-3 pb-3">
            <div
                v-for="(img, index) in displayedImages"
                :key="img.src.thumb_def"
                class="description-tab-bottom-section-right-tiny-img"
                @click="showModal"
            >
                <picture v-if="img.src">
                    <source :srcset="img.src.thumb_webp" type="image/webp">
                    <source :srcset="img.src.thumb_def" type="image/jpg">

                    <img
                        :alt="img.title"
                        :title="img.title"
                    >
                </picture>

                <div
                    v-if="index === displayedImages.length - 1 && extraImagesCount > 0"
                    class="description-tab-bottom-section-right-img-wrap-bottom-label d-flex justify-content-center align-items-center"
                    @click.stop="showModal"
                >
                    <span>+{{ extraImagesCount }} images</span>
                </div>
            </div>
        </div>
       
        <div class="d-flex description-tab-bottom-wrap flex-lg-row flex-column">
            <div
                v-if="description"
                class="description-tab-description-block"
                :class="{'description-tab-description-block--no-data': description && !video?.iframe && !video?.schema}"
            >
                <h3 class="description-tab-title mb-5">
                    Description
                </h3>
                <div class="cl-subtext" v-html="description" />
            </div>
            <div
                v-if="video?.iframe && video?.schema"
                class="description-tab-video-block"
                :class="{'description-tab-video--no-data':!description && video.iframe && video.schema}"
            >
                <h3 class="description-tab-title mb-5" itemprop="name">
                    Video
                </h3>
                <div
                    id="video"
                    itemprop="video"
                    itemscope
                    itemtype="http://schema.org/VideoObject"
                >
                    <meta itemprop="duration" :content="video?.schema.duration">
                    <meta itemprop="uploadDate" :content="video?.schema.uploadDate">
                    <meta itemprop="thumbnailURL" :content="video?.schema.thumbnailURL">
                    <meta itemprop="interactionCount" :content="video?.schema.interactionCount">
                    <meta itemprop="embedURL" :content="video?.schema.embedURL">

                    <div>
                        <div class="embed-responsive embed-responsive-4by3" v-html="video.iframe" />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="conditions">
            <h3 class="description-tab-title mb-5 mt-5" itemprop="name">
                Conditions
            </h3>
            <div
                class="cl-subtext description-tab-conditions"
                v-html="conditions"
            />
        </div>

        <button
            v-if="(liveReview?.editedReview || description || video?.iframe) && !hostelIsClosed"
            class="btn btn-danger border-0 shadow-none btn-lg description-tab-btn d-flex d-lg-none justify-content-center w-100 mt-5"

            @click="emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)"
        >
            Check availability
        </button>
        <a
            v-else-if="hostelIsClosed"
            :href="bookHostelsHref"
            class="btn btn-danger border-0 shadow-none btn-lg d-flex d-lg-none justify-content-center hostelIsClosed mt-5"
            rel="nofollow"
            target="_blank"
            title="Check Hostelworld"
        >
            Check Hostelworld
        </a>
    </div>

    <AppModal
        :show="isModal"
        class="description-tab-modal"
        @close="showModal"
    >
        <DescriptionTabModalContent
            v-if="liveReview"
            :live-review="liveReview"
            @close-modal="showModal"
        >
            <template #header>
                <h2 class="description-tab-modal-title mb-0 mr-sm-3">
                    {{ locationName }}{{ cityName ? `, ${cityName}` : '' }}
                </h2>
                <button
                    v-if="!hostelIsClosed"
                    class="btn btn-danger d-flex ml-md-0 border-0 shadow-none btn-lg description-tab-modal-btn justify-content-center mt-2 mt-sm-0"
                    :title="`book now ${ locationName }`"
                    @click="closeModalAndScrollToPriceTab"
                >
                    Check availability
                </button>
                <a
                    v-else
                    class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg hostelIsClosed"
                    :title="`Check Hostelworld`"
                    :href="bookHostelsHref"
                    rel="nofollow"
                    target="_blank"
                >
                    Check Hostelworld
                </a>
            </template>
        </DescriptionTabModalContent>
        <template #close-icon>
            <div class="close-modal-btn d-flex align-items-center justify-content-center">
                <TheIcon    
                    icon-id="close-icon-2"
                    width="24"
                    height="24"
                    stroke="#4A5268"
                />
            </div>
        </template>
    </AppModal>
</template>

<style lang="scss" scoped>
.description-tab {

    &-gallery {
        gap: 16px;
        &-item {
            max-width: 200px;

            img {
                width: 100%;
                height: auto;
                border-radius: 16px;
                flex-shrink: 0;
            }
        }
    }

    &-bottom-section {
        gap: 16px;

        &-right {

            &-tiny-img {
                max-width: calc((100% / 2 -  16px));
                height: 200px;
                flex: 0 0 auto;
                cursor: pointer;
                position: relative;
                width: 100%;

                @media (min-width: 576px) {
                    max-width: calc((100% - (2 * 16px)) / 3);
                }

                @media (min-width: 768px) {
                    max-width: calc((100% - (3 * 16px)) / 4);
                }

                @media (min-width: 992px) {
                    max-width: calc((100% - (4 * 16px)) / 5);
                }

                @media (min-width: 1200px) {
                    height: 250px;
                }

                img {
                    object-fit: cover;
                    max-width: 100%;
                    height: 100%;
                    border-radius: 16px;
                    width: 100%;
                }
            }

            &-img-wrap-bottom {
                position: relative;
                margin-top: 16px;
                overflow: hidden;
                white-space: nowrap;
                gap: 16px;

                border-bottom: 1px solid var(--accent-bg);

                &-label {
                    position: absolute;
                    background-color: rgba(0, 0, 0, 0.5);
                    height: 100%;
                    cursor: pointer;    
                    right: 0;
                    top: 0;
                    width: 100%;
                    border-radius: 16px;

                    span {
                        color: white;
                        font-size: 16px;
                        line-height: 1.4;
                    }
                } 
            }

            &-swiper {
                &:deep(.listing-swiper) {
                    height: 240px;

                    @media (min-width: 576px) {
                        height: 340px;
                    }
                   
                    .swiper-pagination {
                        bottom: 16px;
                    }

                    img {
                        max-height: 320px;
                        border-radius: 16px;

                        @media (min-width: 576px) {
                           min-height: 340px;
                        }
                    }
                }

                &:deep(.listing-swiper-thumb) {
                    margin-top: 8px;

                    img {
                        border-radius: 8px;
                        height: 72px;

                        @media (min-width: 576px) {
                            height: 120px
                        }
                    }
                }
            }
        }
    }

    &-modal {
        &-title {
            font-family: var(--font-family-poppins-sans-serif);
            font-weight: 600;
            line-height: 1.4;  
            font-size: 16px;

            @media (min-width: 576px) {
                font-size: 20px;
            }
            
            @media (min-width: 992px) {
                font-size: 24px; 
            }
        }

        &-btn {
            border-radius: 8px;
            padding: 0.5rem 1rem;
            color: white;
            width: 100%;

            @media (min-width: 576px) {
                margin-left: auto;
                width: auto;
            }

            @media (min-width: 992px) {
                padding: 0.75rem 1.5rem; 
            }
        }
    }
    
    &:deep(.hidden-gemz) {
        .hg-content h3 {
            font-size: 18px;
            margin-bottom: 16px;

            @media (min-width: 576px) {
                font-size: 20px;
            }

            @media (min-width: 992px) {
                font-size: 24px;
            }

            @media (min-width: 1200px) {
                font-size: 32px;
            }
        }
    }

    &-main-liveReview-title {
        font-size: 18px;

        @media (min-width: 576px) {
            font-size: 20px;
        }

        @media (min-width: 992px) {
            font-size: 1.5rem;
        }
    }

    &-live-review {
        font-size: 16px;
        line-height: 1.8;
        font-weight: 400;
        color: var(--secondary-text);

        &:deep(b) {
            color: var(--main-text);
        }
    }

    &-user {

        &-name {
            font-family: var(--font-family-poppins-sans-serif);
            font-size: 16px;
            font-weight: 600;
            line-height: 1.4;
        }

        &-avatar {
            width: 48px;
            height: 48px;

            img {
                object-fit: cover;
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }
        }

    }

    &-date {
        @media (min-width: 576px) {
            padding: 4px 12px;
            border-radius: 8px;
            background-color: var(--light-bg);
        }
    }

    &-title {
        font-size: 16px;
        line-height: 1.5;
        font-family: var(--font-family-poppins-sans-serif);
        font-style: normal;
        font-weight: 600;

        @media (min-width: 768px) {
            font-size: 20px;
        }

        @media (min-width: 992px) {
            font-size: 24px;
        }
    }

    &-btn {
        outline: none;
        appearance: none;
        border-radius: 8px;
    }

    &-bottom-wrap {
        gap: 32px;
    }


    &-description-block {
        flex-basis: calc(100% - 50% - 16px);
        line-height: 1.8;

        &--no-data {
            flex-basis: 100%;
        }
    }

    &-video-block {
        line-height: 1.8;
        flex-basis: calc(100% - 50% - 16px);

        &--no-data {
            flex-basis: 100%;
        }
    }

    &-conditions {
        line-height: 1.8;
    }
}

.hostelIsClosed {
    background-color: #ff733d;
    transition: background-color 0.3s;

    &:hover {
        background-color: #cc5c30;
        border-color: #cc5c30;
    }
}

.close-modal-btn {
    border-radius: 50%;
    width: 35px;
    height: 35px;
    background-color: var(--light-bg);

    box-shadow: 0px 6px 2px -4px #0E132C14;

    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    @media (min-width: 576px) {
        width: 44px;
        height: 44px;
    }

    &:hover {
        background-color: #e2e6ea;
    }

    &:deep(svg) {
        display: flex;
    }
}
</style>

<style lang="scss">
.description-tab-modal .app-modal-dialog {
    border-radius: 16px;
    height: 95vh;
    width: 95vw;
}

.description-tab-modal .app-modal-close-btn {
    top: 10px;
    right: 10px;

    @media (min-width: 576px) {
        top: 20px;
        right: 24px;
    }

    @media (min-width: 992px) {
       top: 26px;
    }
}

.description-tab-modal .app-modal {
    z-index: 1600;
}

.description-tab-modal {
        .slide-fade-enter-active, .slide-fade-leave-active {
        transition: transform 0.4s ease-out, opacity .5s ease-out;

        .app-modal-dialog {
            transition: transform 0.4s ease-out, opacity .5s ease-out;
        }
    }

    .slide-fade-enter-from {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(120%);
            opacity: 1;
        }
    }

    .slide-fade-enter-to {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;

        }
    }

    .slide-fade-leave-from {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;

        }
    }

    .slide-fade-leave-to {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(120%);
            opacity: 1;
        }
    }
}
</style>